import React, { useState, useEffect } from 'react';
import { X, Phone, PhoneCall, PhoneOff, Clock } from 'lucide-react';
import APIService from '../services/api';

const BASE = 'https://besoins-tat-production.up.railway.app';

// ===== BOUTON + MODAL APPEL AGENT =====
const CallAgentButton = ({ candidat, besoin, questions, showNotification, onCallComplete }) => {
  const [showModal, setShowModal] = useState(false);

  if (!questions?.length) return null;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="bd-mini"
        title="Appeler le candidat avec l'agent IA"
      >
        <Phone className="w-3.5 h-3.5" />
        Appel IA
      </button>

      {showModal && (
        <CallAgentModal
          candidat={candidat}
          besoin={besoin}
          questions={questions}
          showNotification={showNotification}
          onClose={() => setShowModal(false)}
          onCallComplete={onCallComplete}
        />
      )}
    </>
  );
};

// ===== MODAL APPEL =====
// Exportée : la carte candidat l'ouvre directement (menu « Actions » / bouton Appel IA)
export const CallAgentModal = ({ candidat, besoin, questions, showNotification, onClose, onCallComplete }) => {
  const [phone, setPhone] = useState(candidat?.telephone || candidat?.phone || '');
  const [language, setLanguage] = useState('fr');
  const [step, setStep] = useState('form'); // form | calling | done | error
  const [callDbId, setCallDbId] = useState(null);
  // LA LANGUE QUE LE SERVEUR A RETENUE — pas celle qu'on croit avoir choisie
  // (vécu 22-23.09 : trois fois « c'est toujours en français », deux
  // correctifs sur la mauvaise couche). La fenêtre la montre dès le départ de
  // l'appel : un écart entre le choix et ce qui part se voit en une seconde,
  // pas à l'écoute de l'enregistrement.
  const [langueServeur, setLangueServeur] = useState(null);
  const [callStatus, setCallStatus] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [pollingInterval, setPollingInterval] = useState(null);
  // COMMENT L'AGENT VA MENER L'ENTRETIEN. Le réglage vivait dans
  // Configuration → Entretiens : personne n'y va au moment de lancer un appel,
  // et la V2 est restée invisible (vécu 22.09). On le dit ICI, là où la
  // question se pose — et on laisse en changer ceux qui en ont le droit.
  const [conduite, setConduite] = useState(null);
  const [bascule, setBascule] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/config/agent-vocal`, {
          headers: { 'Authorization': `Bearer ${APIService.getToken()}` } });
        if (!r.ok) return;                       // réglage absent : on se tait
        const j = await r.json();
        if (j?.success !== false && j?.versions) setConduite(j);
      } catch { /* la conduite ne bloque jamais un appel */ }
    })();
  }, []);

  const changerConduite = async (cle) => {
    try {
      const r = await fetch(`${BASE}/config/agent-vocal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` },
        body: JSON.stringify({ version: cle }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) { showNotification(j.error || 'Changement impossible', 'error'); return; }
      setConduite(c => ({ ...c, version: j.version }));
      setBascule(false);
      showNotification(j.message || 'Conduite enregistrée.', 'success');
    } catch (e) { showNotification(`Changement impossible : ${e.message}`, 'error'); }
  };

  useEffect(() => {
    return () => { if (pollingInterval) clearInterval(pollingInterval); };
  }, [pollingInterval]);

  useEffect(() => {
    let timer;
    if (step === 'calling') { timer = setInterval(() => setElapsed(e => e + 1), 1000); }
    return () => clearInterval(timer);
  }, [step]);

  const formatTime = s => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;

  const startCall = async () => {
    const cleanPhone = phone.replace(/\s/g, '');
    // Accepte +41..., 0041..., 0..., ou pur numérique. Le backend normalise en E.164.
    if (!cleanPhone.match(/^(\+|00)?[0-9]{8,15}$/)) {
      showNotification('Numéro invalide (ex: +41791234567 ou 0791234567)', 'error');
      return;
    }

    setStep('calling');
    setElapsed(0);

    try {
      const res = await fetch(`${BASE}/calls/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` },
        body: JSON.stringify({
          candidatId: candidat.id,
          besoinId: besoin.id,
          candidatNom: candidat.nom,
          titreBesoin: besoin.titre,
          phone: cleanPhone,
          questions,
          // LA LANGUE CHOISIE PART AVEC L'APPEL. Vécu 22 et 23.09 : « le call
          // part toujours en français malgré qu'on ait choisi anglais ». Le
          // sélecteur ci-dessus changeait `language`, mais ce corps ne
          // l'envoyait pas — le serveur recevait un appel sans langue et
          // prenait le français par défaut. Deux correctifs serveur
          // (phrases prononcées, questions traduites) étaient justes et ne
          // s'appliquaient jamais. Seul l'appel en lot envoyait la sienne.
          language
        })
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Erreur démarrage appel');

      console.log('📞 Appel initié, dbId:', data.dbId);
      setCallDbId(data.dbId);
      if (data.langue_libelle) {
        setLangueServeur({ libelle: data.langue_libelle, repli: data.langue_par_defaut === true,
          avertissement: data.langue_avertissement || null,
          questions: data.questions_avertissement || null });
      }

      // Polling pour détecter la fin de l'appel + rapport généré
      const interval = setInterval(async () => {
        try {
          const statusRes = await fetch(`${BASE}/calls/status/${data.dbId}`, {
            headers: { 'Authorization': `Bearer ${APIService.getToken()}` }
          });
          const statusData = await statusRes.json();
          if (statusData.success) {
            setCallStatus(statusData.call);
            if (statusData.call.statut === 'evaluated') {
              clearInterval(interval);
              setStep('done');
              showNotification('Entretien terminé — rapport généré !', 'success');
              if (onCallComplete) onCallComplete();
            } else if (statusData.call.statut === 'error') {
              clearInterval(interval);
              setStep('error');
            }
          }
        } catch (e) { console.warn('Polling error:', e); }
      }, 5000); // poll toutes les 5s

      setPollingInterval(interval);
      showNotification(`Appel en cours vers ${cleanPhone}...`, 'success');

    } catch (error) {
      console.error('Erreur appel:', error);
      showNotification('Erreur: ' + error.message, 'error');
      setStep('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className={`p-5 flex items-center justify-between ${
          step === 'calling' ? 'bg-gray-900' :
          step === 'done' ? 'bg-gray-900' :
          step === 'error' ? 'bg-red-600' : 'bg-gray-800'
        } text-white`}>
          <div className="flex items-center gap-3">
            {step === 'calling' ? <PhoneCall className="w-5 h-5 animate-pulse" /> :
             step === 'done' ? <Phone className="w-5 h-5" /> :
             step === 'error' ? <PhoneOff className="w-5 h-5" /> :
             <Phone className="w-5 h-5" />}
            <div>
              <h3 className="font-bold text-sm">Agent IA — Entretien téléphonique</h3>
              <p className="text-xs opacity-80">{candidat.nom} · {besoin.titre}</p>
            </div>
          </div>
          {step !== 'calling' && (
            <button onClick={onClose} className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-6">
          {/* ÉTAPE : FORMULAIRE */}
          {step === 'form' && (
            <div className="space-y-5">
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-600">
                <p className="font-medium text-gray-800 mb-2">L'agent va :</p>
                <ul className="space-y-1 text-xs">
                  <li>📞 Appeler le candidat sur son mobile</li>
                  <li>🎤 Poser les {questions.length} questions techniques en français</li>
                  <li>Générer automatiquement le rapport après l'appel</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Numéro de téléphone du candidat
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+41 79 123 45 67"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-gray-400 outline-none"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter' && phone) startCall(); }}
                />
                <p className="text-xs text-gray-400 mt-1">Formats acceptés : +41791234567, 0041791234567, 0791234567</p>
              </div>

              {/* LA CONDUITE DE L'ENTRETIEN — dite ici, pas seulement dans une
                  page de configuration que personne n'ouvre en lançant un
                  appel. On NOMME celle qui va s'appliquer ; la changer reste
                  réservé aux administrateurs, et c'est le serveur qui le dit
                  (`modifiable`), pas l'écran qui le devine.
                  Les couleurs passent par les tokens : ce fichier est en
                  Tailwind historique, on n'y ajoute pas de palette littérale. */}
              {conduite?.versions?.length > 0 && (() => {
                const active = conduite.versions.find(v => v.cle === conduite.version) || conduite.versions[0];
                const etiquetteBeta = { fontSize: '10px', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.05em', padding: '2px 6px', borderRadius: '5px',
                  background: 'var(--erp-warning-soft)', color: 'var(--erp-warning)' };
                return (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-2)' }}>
                      Conduite de l'entretien
                    </label>
                    <div className="rounded-xl p-3" style={{ border: '1px solid var(--hair)' }}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <span className="text-sm font-medium flex items-center gap-2 flex-wrap"
                          style={{ color: 'var(--text-strong)' }}>
                          {active.libelle}
                          {active.beta && <span style={etiquetteBeta}>bêta</span>}
                        </span>
                        {conduite.modifiable && conduite.versions.length > 1 && (
                          <button type="button" onClick={() => setBascule(b => !b)}
                            className="text-xs font-medium underline underline-offset-2"
                            style={{ color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', font: 'inherit' }}>
                            {bascule ? 'Garder celle-ci' : 'Changer'}
                          </button>
                        )}
                      </div>
                      <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-3)' }}>{active.resume}</p>

                      {bascule && (
                        <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid var(--hair)' }}>
                          <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                            Le choix vaut pour toute l'agence.
                          </p>
                          {/* Ce que le changement emporte : la version n'est pas
                              figée à la planification, elle est lue au moment où
                              l'appel part. Dit avant le clic, jamais après. */}
                          {conduite.avertissement_planifies && (
                            <p className="text-xs rounded-lg px-3 py-2" style={{ lineHeight: 1.5,
                              border: '1px solid var(--erp-warning)', background: 'var(--erp-warning-soft)',
                              color: 'var(--erp-warning)' }}>
                              {conduite.avertissement_planifies}
                            </p>
                          )}
                          {conduite.versions.filter(v => v.cle !== conduite.version).map(v => (
                            <button key={v.cle} type="button" onClick={() => changerConduite(v.cle)}
                              className="w-full text-left rounded-lg px-3 py-2"
                              style={{ border: '1px solid var(--hair)', background: 'var(--surface)',
                                cursor: 'pointer', font: 'inherit' }}>
                              <span className="text-sm font-medium flex items-center gap-2 flex-wrap"
                                style={{ color: 'var(--text-strong)' }}>
                                {v.libelle}
                                {v.beta && <span style={etiquetteBeta}>bêta</span>}
                              </span>
                              <span className="block text-xs mt-0.5 leading-relaxed" style={{ color: 'var(--text-3)' }}>
                                {v.resume}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Langue de l'entretien</label>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { code: 'fr', label: '🇫🇷 Français' },
                    { code: 'en', label: '🇬🇧 English' },
                    { code: 'de', label: '🇩🇪 Deutsch' },
                    { code: 'it', label: '🇮🇹 Italiano' },
                  ].map(l => (
                    <button key={l.code} type="button"
                      onClick={() => setLanguage(l.code)}
                      className={`px-2 py-2 rounded-xl text-xs font-medium border transition ${language === l.code ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                      {l.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                Assurez-vous que le candidat est disponible et attend votre appel.
                L'entretien dure environ 15-20 minutes.
              </div>

              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200 transition">
                  Annuler
                </button>
                <button onClick={startCall} disabled={!phone.trim()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black disabled:opacity-40 transition">
                  <Phone className="w-4 h-4" /> Lancer l'appel
                </button>
              </div>
            </div>
          )}

          {/* ÉTAPE : EN COURS */}
          {step === 'calling' && (
            <div className="text-center space-y-6 py-4">
              <div className="relative mx-auto w-20 h-20">
                <div className="absolute inset-0 rounded-full bg-gray-100 animate-ping opacity-75" />
                <div className="relative w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center">
                  <PhoneCall className="w-8 h-8 text-white" />
                </div>
              </div>

              <div>
                <p className="text-lg font-bold text-gray-900">Appel en cours</p>
                <p className="text-sm text-gray-500 mt-1">L'agent pose les questions à {candidat.nom}</p>
                {/* Ce que le SERVEUR a retenu, dit tel quel. Un repli sur le
                    français est un avertissement, pas une ligne grise. */}
                {langueServeur && (
                  <p role="status" style={{ marginTop: '8px', fontSize: '12.5px', fontWeight: 600,
                    color: langueServeur.repli ? 'var(--erp-warning)' : 'var(--erp-text-secondary)' }}>
                    {langueServeur.repli
                      ? (langueServeur.avertissement || "La langue choisie n'a pas été reçue : l'entretien part en français.")
                      : `Entretien en ${langueServeur.libelle}`}
                  </p>
                )}
                {langueServeur?.questions && (
                  <p role="status" style={{ marginTop: '4px', fontSize: '12px', color: 'var(--erp-warning)' }}>
                    {langueServeur.questions}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-center gap-2 text-gray-700 font-mono text-xl font-bold">
                <Clock className="w-5 h-5" />
                {formatTime(elapsed)}
              </div>

              <div className="bg-gray-50 rounded-xl p-4 text-left space-y-2">
                <p className="text-xs font-medium text-gray-500">Questions posées :</p>
                {questions.slice(0, 3).map((q, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="w-4 h-4 bg-gray-900 text-white text-xs rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">{i+1}</span>
                    <p className="text-xs text-gray-600 truncate">{q.question}</p>
                  </div>
                ))}
                {questions.length > 3 && <p className="text-xs text-gray-400 pl-6">+{questions.length - 3} autres questions...</p>}
              </div>

              <p className="text-xs text-gray-400">
                Le rapport sera généré automatiquement à la fin de l'appel.
                Cette fenêtre se mettra à jour automatiquement.
              </p>
            </div>
          )}

          {/* ÉTAPE : TERMINÉ */}
          {step === 'done' && (
            <div className="text-center space-y-5 py-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                <span className="text-3xl">✓</span>
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">Entretien terminé !</p>
                <p className="text-sm text-gray-500 mt-1">
                  Le rapport a été généré et est disponible sur la fiche du candidat.
                </p>
              </div>
              {callStatus?.score && (
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                  callStatus.score >= 70 ? 'bg-gray-100 text-gray-700' :
                  callStatus.score >= 50 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                }`}>
                  Score : {callStatus.score}% · {callStatus.recommandation}
                </div>
              )}
              {callStatus?.audio_url && (
                <div className="mt-3 p-3 bg-gray-50 rounded-xl text-sm">
                  <p className="font-medium text-gray-700 mb-1">🎙️ Enregistrement disponible</p>
                  <div className="flex items-center gap-2">
                    <code className="bg-white px-2 py-1 rounded text-xs font-mono border border-gray-200">
                      Password : {callStatus.audio_password}
                    </code>
                    {/* Le mot de passe VOYAGE dans le lien : sans lui, la page
                        s'ouvrait sur une demande de code que l'utilisateur
                        devait recopier d'à côté. Le code reste affiché pour
                        qui veut partager le lien à un tiers. */}
                    <a href={`https://besoins-tat-production.up.railway.app/calls/audio/${callDbId}${callStatus.audio_password ? `?pwd=${encodeURIComponent(callStatus.audio_password)}` : ''}`}
                       target="_blank" rel="noreferrer"
                       className="text-xs text-gray-700 underline hover:text-gray-900">
                      Écouter →
                    </a>
                  </div>
                </div>
              )}
              <button onClick={() => { onClose(); if (onCallComplete) onCallComplete(); }}
                className="w-full px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black transition">
                Voir le rapport
              </button>
            </div>
          )}

          {/* ÉTAPE : ERREUR */}
          {step === 'error' && (
            <div className="text-center space-y-5 py-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                <PhoneOff className="w-8 h-8 text-red-500" />
              </div>
              <div>
                <p className="text-lg font-bold text-gray-900">Erreur lors de l'appel</p>
                <p className="text-sm text-gray-500 mt-1">
                  Vérifiez que Twilio est bien configuré dans ElevenLabs et que le numéro est valide.
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep('form')}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200 transition">
                  Réessayer
                </button>
                <button onClick={onClose}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl text-sm hover:bg-red-700 transition">
                  Fermer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallAgentButton;

// ===== BATCH CALLING =====
export const BatchCallButton = ({ candidats, besoin, questions, showNotification, onBatchComplete }) => {
  const [showModal, setShowModal] = useState(false);

  const eligibles = candidats.filter(c => c.telephone || c.phone);
  if (!questions?.length || !candidats?.length) return null;

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="bd-mini"
        title={eligibles.length < candidats.length ? `${candidats.length - eligibles.length} candidat(s) sans numéro` : 'Appeler tous les candidats avec l\'agent IA'}
      >
        <Phone className="w-3.5 h-3.5" />
        Appel batch ({candidats.length})
      </button>

      {showModal && (
        <BatchCallModal
          candidats={candidats}
          besoin={besoin}
          questions={questions}
          showNotification={showNotification}
          onClose={() => setShowModal(false)}
          onComplete={onBatchComplete}
        />
      )}
    </>
  );
};

const BatchCallModal = ({ candidats, besoin, questions, showNotification, onClose, onComplete }) => {
  const [phones, setPhones] = useState(() =>
    Object.fromEntries(candidats.map(c => [c.id, c.telephone || c.phone || '']))
  );
  const [languages, setLanguages] = useState(() =>
    Object.fromEntries(candidats.map(c => [c.id, 'fr']))
  );
  const [step, setStep] = useState('form'); // form | running | done
  const [results, setResults] = useState([]);

  const allHavePhone = candidats.every(c => phones[c.id]?.trim());

  const startBatch = async () => {
    setStep('running');
    try {
      // Un appel par candidat avec sa propre langue
      const res = await fetch(`${BASE}/calls/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${APIService.getToken()}` },
        body: JSON.stringify({
          calls: candidats.map(c => ({
            candidatId: c.id,
            besoinId: besoin.id,
            candidatNom: c.nom,
            titreBesoin: besoin.titre,
            phone: phones[c.id].trim(),
            language: languages[c.id] || 'fr'
          })),
          questions
        })
      });
      const data = await res.json();
      setResults(data.results || []);
      setStep('done');
      if (onComplete) onComplete();
    } catch (e) {
      showNotification('Erreur batch: ' + e.message, 'error');
      setStep('form');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[90] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Header */}
        <div className="p-5 flex items-center justify-between bg-gray-900 text-white">
          <div className="flex items-center gap-3">
            <Phone className="w-5 h-5" />
            <div>
              <h3 className="font-bold text-sm">Appel batch — {candidats.length} candidats</h3>
              <p className="text-xs opacity-80">{besoin.titre}</p>
            </div>
          </div>
          {step !== 'running' && (
            <button onClick={onClose} className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">

          {/* FORM */}
          {step === 'form' && (
            <>
              {/* Numéros + langue par candidat */}
              <div>
                <div className="grid grid-cols-[1fr_auto_140px] gap-2 mb-1 px-1">
                  <span className="text-xs font-medium text-gray-400">Candidat</span>
                  <span className="text-xs font-medium text-gray-400">Langue</span>
                  <span className="text-xs font-medium text-gray-400">Téléphone</span>
                </div>
                <div className="space-y-2">
                  {candidats.map(c => (
                    <div key={c.id} className="grid grid-cols-[1fr_auto_140px] items-center gap-2">
                      <span className="text-sm text-gray-700 truncate font-medium">{c.nom || c.name || `Candidat ${c.id}`}</span>
                      <div className="flex gap-1">
                        {[{code:'fr',flag:'🇫🇷'},{code:'en',flag:'🇬🇧'},{code:'de',flag:'🇩🇪'},{code:'it',flag:'🇮🇹'}].map(l => (
                          <button key={l.code} onClick={() => setLanguages(prev => ({...prev, [c.id]: l.code}))}
                            className={`w-7 h-7 rounded-lg text-sm transition border ${languages[c.id] === l.code ? 'bg-gray-900 border-gray-900' : 'bg-white border-gray-200 hover:border-gray-400'}`}>
                            {l.flag}
                          </button>
                        ))}
                      </div>
                      <input
                        type="tel"
                        value={phones[c.id] || ''}
                        onChange={e => setPhones(prev => ({ ...prev, [c.id]: e.target.value }))}
                        placeholder="+41 79..."
                        className="border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-gray-400 outline-none w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button onClick={onClose} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm hover:bg-gray-200">Annuler</button>
                <button onClick={startBatch} disabled={!allHavePhone}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black disabled:opacity-40">
                  <Phone className="w-4 h-4" /> Lancer {candidats.length} appels
                </button>
              </div>
            </>
          )}

          {/* RUNNING */}
          {step === 'running' && (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                <Phone className="w-8 h-8 text-gray-700 animate-pulse" />
              </div>
              <p className="font-semibold text-gray-800">Lancement des appels...</p>
              <p className="text-sm text-gray-500">{candidats.length} appels en cours d'initiation</p>
            </div>
          )}

          {/* DONE */}
          {step === 'done' && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700">Résultats :</p>
              {results.map((r, i) => (
                <div key={i} className={`flex items-center justify-between p-3 rounded-xl text-sm ${r.success ? 'bg-gray-50 border border-gray-200' : 'bg-red-50 border border-red-100'}`}>
                  <span className="font-medium text-gray-800">{r.candidatNom?.split(' ').slice(-1)[0]}</span>
                  <span className={r.success ? 'text-gray-700 font-medium' : 'text-red-500'}>
                    {r.success ? 'Appel lancé' : `${r.error?.substring(0, 40)}`}
                  </span>
                </div>
              ))}
              <button onClick={() => { onClose(); if (onComplete) onComplete(); }}
                className="w-full mt-2 px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-black">
                Fermer
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
